import { Button, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';

const SEEN_KEY = 'bg_swipe_hint_seen';
const DWELL_MS = 30_000;
const FIRST_VISIT_DELAY_MS = 900;
const MOBILE_MAX = 768;

function hasSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}
function markSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    // ignore (Private Mode etc.)
  }
}

/**
 * Kurzanleitung fürs Swipen. Erscheint beim ERSTBESUCH (einmalig) und — als sanfte
 * Erinnerung — wenn man am Handy 30 s ohne zu wischen auf der Seite bleibt.
 * `activityKey` (die aktuelle Objekt-ID) setzt den Dwell-Timer bei jedem Wisch zurück.
 */
export function SwipeHint({ activityKey }: { activityKey: number | undefined }): JSX.Element | null {
  const [show, setShow] = useState(false);
  const dismissedThisSession = useRef(false);

  // Erstbesuch: kurz nach dem Laden einblenden.
  useEffect(() => {
    if (hasSeen()) return;
    const t = setTimeout(() => setShow(true), FIRST_VISIT_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  // Langes Verweilen ohne Aktion (nur mobil): nach 30 s einblenden. Reset bei jedem Wisch.
  useEffect(() => {
    if (dismissedThisSession.current) return;
    if (typeof window !== 'undefined' && window.innerWidth >= MOBILE_MAX) return;
    const t = setTimeout(() => setShow(true), DWELL_MS);
    return () => clearTimeout(t);
  }, [activityKey]);

  const close = (): void => {
    setShow(false);
    dismissedThisSession.current = true;
    markSeen();
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: 16,
        paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 440,
          background: '#fff',
          borderRadius: 18,
          padding: 20,
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        <Stack gap="sm">
          <Text fw={800} size="lg">
            🏠 So wischst du
          </Text>
          <Stack gap="xs">
            <HintRow icon="👉" accent="#059669" title="Rechts = Hot" desc="an Partner zum Bewerten schicken" />
            <HintRow icon="👈" accent="#dc2626" title="Links = Pass" desc="aussortieren" />
            <HintRow icon="👆" accent="#2563eb" title="Antippen" desc="öffnet das Inserat beim Anbieter" />
            <HintRow icon="⬆️" accent="#2563eb" title="Nach oben wischen" desc="öffnet das Inserat — die Immobilie bleibt in der Liste" />
          </Stack>
          <Text size="xs" c="dimmed">
            Oder unten die Buttons <b>Pass</b> / <b>Hot</b> nutzen. Am PC: Pfeiltasten ← / →.
          </Text>
          <Button fullWidth radius="md" onClick={close}>
            Alles klar
          </Button>
        </Stack>
      </div>
    </div>
  );
}

function HintRow({
  icon,
  accent,
  title,
  desc,
}: {
  icon: string;
  accent: string;
  title: string;
  desc: string;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <span style={{ fontSize: '1.3rem', lineHeight: 1.3 }}>{icon}</span>
      <Text size="sm" style={{ flex: 1 }}>
        <b style={{ color: accent }}>{title}</b> — {desc}
      </Text>
    </div>
  );
}
